import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Presentation, Video, Image, Link2, ClipboardList, X, Play, Pause, Loader2 } from 'lucide-react';
import { useYearContext } from '../components/Layout.jsx';
import { Empty } from '../components/states.jsx';
import { formatDate } from '../lib/date.js';
import { playback, isPhoto, formatLength } from '../lib/media.js';
import MediaPlayer from '../components/MediaPlayer.jsx';

const MATERIAL_KINDS = {
  slides:    { label: 'Slajdy', Icon: Presentation },
  recording: { label: 'Záznam', Icon: Video },
  photo:     { label: 'Fotka', Icon: Image },
};

const FILTERS = [
  { id: 'slides', label: 'Se slajdy' },
  { id: 'recording', label: 'Se záznamem' },
  { id: 'photo', label: 'S fotkou' },
  { id: 'assignment', label: 'S úkolem' },
];

/** Diakritiku ignorujeme, ať se dá hledat i bez háčků. */
const normalize = (text) =>
  String(text).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function MaterialChip({ material, open, onToggle }) {
  const kind = MATERIAL_KINDS[material.kind];
  const label = material.label || kind?.label || 'Materiál';
  const here = playback(material);

  // Nahraná fotka se rozbalí pod hodinou, odkaz jinam zůstává odkazem.
  if (isPhoto(material)) {
    return (
      <button
        type="button"
        className="chip chip-accent chip-play"
        onClick={onToggle}
        aria-expanded={open}
      >
        <Image size={13} aria-hidden="true" />
        {label}
      </button>
    );
  }

  // Nahraný záznam se přehraje na místě, odkaz jinam zůstává odkazem.
  if (here?.ready) {
    const length = formatLength(here.duration);
    return (
      <button
        type="button"
        className="chip chip-accent chip-play"
        onClick={onToggle}
        aria-expanded={open}
      >
        {open ? <Pause size={13} aria-hidden="true" /> : <Play size={13} aria-hidden="true" />}
        {label}
        {length && <small>{length}</small>}
      </button>
    );
  }

  if (here?.processing) {
    return (
      <span className="chip chip-quiet" title="Záznam se převádí do formátu, který přehraje každý prohlížeč.">
        <Loader2 size={13} aria-hidden="true" className="spin" />
        {label} se zpracovává{here.progress ? ` (${here.progress} %)` : '…'}
      </span>
    );
  }

  const Icon = kind?.Icon || Link2;
  const external = /^https?:\/\//i.test(material.url);

  return (
    <a
      href={material.url}
      className="chip chip-accent"
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      <Icon size={13} aria-hidden="true" />
      {label}
    </a>
  );
}

/** Popisek fotky — vlastní, jinak aspoň hodina, ať čtečka neřekne jen „obrázek". */
const photoAlt = (lecture, material) =>
  material.label || `Fotka z ${lecture.number}. hodiny — ${lecture.title}`;

function Lecture({ lecture, basePath }) {
  const [open, setOpen] = useState(null);
  const materials = lecture.materials || [];
  const shown = open === null ? null : materials[open];
  const shownPlayback = shown ? playback(shown) : null;

  return (
    <article className="lecture">
      <div className="lecture-num" aria-hidden="true">{lecture.number}</div>

      <h2 className="lecture-title">
        <span className="sr-only">{lecture.number}. hodina: </span>
        {lecture.title}
      </h2>

      {lecture.date && <div className="lecture-date">{formatDate(lecture.date)}</div>}

      {(lecture.materials?.length > 0 || lecture.links?.length > 0 || lecture.assignmentNumber) && (
        <div className="chip-row">
          {materials.map((material, index) => (
            <MaterialChip
              key={index}
              material={material}
              open={open === index}
              onToggle={() => setOpen((current) => (current === index ? null : index))}
            />
          ))}

          {lecture.assignmentNumber != null && (
            <Link to={`${basePath}/ukoly#ukol-${lecture.assignmentNumber}`} className="chip chip-warn">
              <ClipboardList size={13} aria-hidden="true" />
              Úkol {lecture.assignmentNumber}
            </Link>
          )}

          {lecture.links?.map((link, index) => {
            const external = /^https?:\/\//i.test(link.url);
            return (
              <a
                key={index}
                href={link.url}
                className="chip chip-quiet"
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : { download: true })}
              >
                <Link2 size={13} aria-hidden="true" />
                {link.label}
              </a>
            );
          })}
        </div>
      )}

      {shownPlayback?.ready && (
        <div className="lecture-player">
          <MediaPlayer
            src={shown.url}
            kind={shownPlayback.play}
            poster={shownPlayback.poster}
            title={`${lecture.number}. hodina — ${lecture.title}`}
            downloadName={`${lecture.number}. hodina — ${lecture.title}`}
          />
        </div>
      )}

      {shown && isPhoto(shown) && (
        <figure className="lecture-photo">
          {/* v plné velikosti se fotka otevře na vlastní kartě, ať jde přiblížit */}
          <a href={shown.url} target="_blank" rel="noopener noreferrer">
            <img src={shown.url} alt={photoAlt(lecture, shown)} loading="lazy" />
          </a>
          <figcaption>
            {photoAlt(lecture, shown)}
            <a href={shown.url} target="_blank" rel="noopener noreferrer">Otevřít v plné velikosti</a>
          </figcaption>
        </figure>
      )}
    </article>
  );
}

export default function Lectures() {
  const { year, basePath } = useYearContext();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState([]);

  const lectures = year.lectures || [];

  const visible = useMemo(() => {
    const needle = normalize(query.trim());

    return lectures.filter((lecture) => {
      if (needle && !normalize(lecture.title).includes(needle)) {
        const inLinks = (lecture.links || []).some((l) => normalize(l.label).includes(needle));
        if (!inLinks) return false;
      }
      // filtry se sčítají – hodina musí splnit všechny zapnuté
      return active.every((filter) =>
        filter === 'assignment'
          ? lecture.assignmentNumber != null
          : (lecture.materials || []).some((m) => m.kind === filter),
      );
    });
  }, [lectures, query, active]);

  const toggle = (id) =>
    setActive((current) => (current.includes(id) ? current.filter((f) => f !== id) : [...current, id]));

  const filtering = query.trim() !== '' || active.length > 0;

  if (lectures.length === 0) {
    return (
      <>
        <div className="page-head"><h1>Hodiny</h1></div>
        <Empty title="Zatím jsme se nesešli">
          Jakmile hodiny začnou, najdeš tu slajdy, záznamy, fotky a odkazy z každé z nich.
        </Empty>
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <h1>Hodiny</h1>
        <p>Slajdy, záznamy, fotky a doplňkové materiály ke každé hodině.</p>
      </div>

      <div className="toolbar">
        <div className="search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Hledat v hodinách…"
            aria-label="Hledat v hodinách"
          />
        </div>

        <div className="filters">
          {FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className="chip filter"
              aria-pressed={active.includes(filter.id)}
              onClick={() => toggle(filter.id)}
            >
              {filter.label}
            </button>
          ))}
          {filtering && (
            <button
              type="button"
              className="chip filter"
              onClick={() => { setQuery(''); setActive([]); }}
            >
              <X size={13} aria-hidden="true" /> Zrušit
            </button>
          )}
        </div>
      </div>

      <p className="sr-only" role="status">
        {filtering ? `Zobrazeno ${visible.length} z ${lectures.length} hodin.` : `Celkem ${lectures.length} hodin.`}
      </p>

      {visible.length === 0 ? (
        <Empty title="Nic neodpovídá">Zkus jiné hledání nebo vypni některý filtr.</Empty>
      ) : (
        <div className="stack">
          {visible.map((lecture) => (
            <Lecture key={lecture.id} lecture={lecture} basePath={basePath} />
          ))}
        </div>
      )}
    </>
  );
}
